import { useMemo, useState } from "react";
import useAdminQuery from "../../lib/queries/useAdminQuery";
import { Topbar } from "../../components/AppShell";
import { SIGNUP_FUNNEL_SQL, mapRows, METRIC_OPTIONS } from "./sql";
import type { UserRow, FunnelStep } from "./sql";
import { previousMonth, fmtPct, fmtN, groupCountry } from "../../lib/funnel/helpers";
import { FiltersBar, applyFilters, applyNonTimeFilters, filtersToPills } from "../../lib/funnel/filters";
import type { Filters } from "../../lib/funnel/filters";
import type { FilterPillSpec } from "../../components/FilterPills";
import { KpiCard, EmptyState } from "../../lib/funnel/charts";
import { QueryInspector } from "../../lib/funnel/QueryInspector";
import { ButterflyChart } from "./ButterflyChart";
import { UserTable } from "./UserTable";
import { FunnelColumnLists } from "./FunnelColumnLists";
import { ActivationOverTime } from "./ActivationOverTime";
import { CohortGrid } from "./CohortGrid";
import { LoadingPanel } from "../../components/LoadingPanel";
import type { WeekKey, PathFilter, SignupCohortEntry } from "./CohortGrid";
import { collapseToDistinctUsers, distinctUserCount } from "./helpers";
import { SignupFunnelExplain, SignupFunnelSchema } from "./explainBlocks";
import { FeatureLiftTable } from "./panels/FeatureLiftTable";
import { OnboardingPathGraph } from "./panels/OnboardingPathGraph";
import { DropoffCohorts } from "./panels/DropoffCohorts";

export function SignupFunnel() {
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [showAbs, setShowAbs] = useState(false);
  const [splitPaid, setSplitPaid] = useState(false);
  const [highlightedUserId, setHighlightedUserId] = useState<string | null>(null);
  const [metricKey, setMetricKey] = useState<string>("paid");
  const [selectedWeek, setSelectedWeek] = useState<WeekKey>("now");
  const [pathFilter, setPathFilter] = useState<PathFilter>("all");
  const metric = useMemo<FunnelStep>(
    () => METRIC_OPTIONS.find((s) => s.key === metricKey) ?? (METRIC_OPTIONS[METRIC_OPTIONS.length - 1] as FunnelStep),
    [metricKey],
  );
  const [filters, setFilters] = useState<Filters>(() => ({
    months: [previousMonth()],
    countries: [],
    roles: [],
    companySizes: [],
    excludeSupport: true,
    granularity: "month",
    cohortDayFrom: null,
    cohortDayTo: null,
    demoBucketing: "signup_date",
  }));

  const result = useAdminQuery(SIGNUP_FUNNEL_SQL);

  // Per-pair rowset. The user_roles spine returns one row per (user, company);
  // duplicates are kept by design.
  const rawPairRows = useMemo<UserRow[]>(() => {
    if (!result.data) return [];
    return mapRows(result.data.columns, result.data.rows as unknown[][]);
  }, [result.data]);

  const availableCountries = useMemo(() => {
    const set = new Set<string>();
    rawPairRows.forEach((u) => set.add(groupCountry(u.country_code)));
    return Array.from(set);
  }, [rawPairRows]);

  const availableRoles = useMemo(() => {
    const set = new Set<string>();
    rawPairRows.forEach((u) => set.add(u.user_role ?? "unknown"));
    return Array.from(set).sort();
  }, [rawPairRows]);

  // Filter-then-distinct: apply every filter to the per-pair rowset first, then
  // collapse to distinct users. A user who is admin in workspace A and support
  // in workspace B keeps their admin row when excludeSupport is on.
  const filteredPairRows = useMemo(() => applyFilters(rawPairRows, filters), [rawPairRows, filters]);
  const distinctUsers = useMemo(() => collapseToDistinctUsers(filteredPairRows), [filteredPairRows]);

  // Cohort grid + activation line chart drive their own time axis (cohort
  // month columns, week buckets), so they ignore time-based filters but apply
  // every non-time filter. Also collapsed to distinct users.
  const cohortPairRows = useMemo(
    () => applyNonTimeFilters(rawPairRows, filters),
    [rawPairRows, filters],
  );
  const cohortDistinctUsers = useMemo(() => collapseToDistinctUsers(cohortPairRows), [cohortPairRows]);
  const cohortGridSignup = useMemo<SignupCohortEntry[]>(() => {
    const bySignupDate = (filters.demoBucketing ?? "first_demo_date") === "signup_date";
    return cohortDistinctUsers.map((u) => ({
      cohortDate: !bySignupDate && u.had_demo && u.first_demo_date ? u.first_demo_date : u.created_at_utc,
      signupDate: u.created_at_utc,
      isDemoPath: u.had_demo,
      user: u,
    }));
  }, [cohortDistinctUsers, filters.demoBucketing]);

  const pagePills = useMemo<FilterPillSpec[]>(() => filtersToPills(filters), [filters]);

  return (
    <>
      <Topbar crumbs={["Dashboards", "Sign-up Activation"]} />
      <div className="page">
        <div className="page-header">
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <h1 style={{ margin: 0 }}>Sign-up Activation</h1>
              <button
                className="schema-info-btn"
                onClick={() => setInspectorOpen(true)}
                title="Inspect query"
                aria-label="Inspect query"
              >
                <i className="ti ti-info-circle" />
              </button>
            </div>
            <p className="lede">
              Of users who signed up, what % activate, which features predict paid conversion, and where do we lose them?
              Spine is <code>public.user_roles</code> (active rows); one row per (user, workspace).
            </p>
          </div>
        </div>

        {inspectorOpen && (
          <QueryInspector
            title="Sign-up Activation query inspector"
            open={inspectorOpen}
            onClose={() => setInspectorOpen(false)}
            schemaContent={<SignupFunnelSchema />}
            explainContent={<SignupFunnelExplain />}
            queryResult={result.data ? { columns: result.data.columns, rows: result.data.rows as unknown[][], rowCount: result.data.rowCount } : null}
            csvFilenamePrefix="signup-funnel-raw"
            filters={filters}
            sqlQueries={[{ label: "Sign-up Activation query", sql: SIGNUP_FUNNEL_SQL }]}
          />
        )}

        <FiltersBar
          filters={filters}
          onChange={setFilters}
          availableCountries={availableCountries}
          availableRoles={availableRoles}
          usersInView={distinctUsers.length}
          cohortVariant="single-with-day"
          showDemoPropagation
        />

        {result.isLoading ? (
          <LoadingPanel />
        ) : result.isError ? (
          <p style={{ color: "var(--fg-danger)" }}>{(result.error as Error).message}</p>
        ) : rawPairRows.length === 0 ? (
          <EmptyState message="No users match this cohort window." />
        ) : distinctUsers.length === 0 ? (
          <EmptyState message="No users match your filter combination. Try widening the filters." />
        ) : (
          <>
            <KpiRow users={distinctUsers} pairRowCount={filteredPairRows.length} />
            <ButterflyChart
              users={distinctUsers}
              showAbs={showAbs}
              onShowAbsChange={setShowAbs}
              splitPaid={splitPaid}
              onSplitPaidChange={setSplitPaid}
              pagePills={pagePills}
            />
            <FunnelColumnLists
              users={distinctUsers}
              splitPaid={splitPaid}
              highlightedUserId={highlightedUserId}
              onHighlightUser={setHighlightedUserId}
              pagePills={pagePills}
            />
            <CohortGrid
              signupCohort={cohortGridSignup}
              metric={metric}
              onMetricChange={setMetricKey}
              pathFilter={pathFilter}
              onPathFilterChange={setPathFilter}
              selectedWeek={selectedWeek}
              onSelectWeek={setSelectedWeek}
              pagePills={pagePills}
            />
            <ActivationOverTime
              signupCohort={cohortGridSignup}
              metric={metric}
              selectedWeek={selectedWeek}
              onMetricChange={setMetricKey}
              pathFilter={pathFilter}
              onPathFilterChange={setPathFilter}
              pagePills={pagePills}
            />
            <FeatureLiftTable users={distinctUsers} pagePills={pagePills} />
            <OnboardingPathGraph users={distinctUsers} pagePills={pagePills} />
            <DropoffCohorts users={distinctUsers} filters={filters} pagePills={pagePills} />
            <UserTable
              users={filteredPairRows}
              highlightedUserId={highlightedUserId}
              onHighlightUser={setHighlightedUserId}
              filters={filters}
              pagePills={pagePills}
            />
          </>
        )}
      </div>
    </>
  );
}

function KpiRow({ users, pairRowCount }: { users: UserRow[]; pairRowCount: number }) {
  const total = users.length;
  const inactive = users.filter((u) => u.synopsis_count === 0 && u.transcript_count === 0).length;
  const active = total - inactive;
  const demoUsers = users.filter((u) => u.had_demo);
  const selfUsers = users.filter((u) => !u.had_demo);
  const demoConverted = demoUsers.filter((u) => u.on_paid_plan).length;
  const selfConverted = selfUsers.filter((u) => u.on_paid_plan).length;
  const workspaceHint = pairRowCount !== total ? `${distinctUserCount(users).toLocaleString()} users / ${pairRowCount.toLocaleString()} workspace rows` : undefined;
  return (
    <div className="kpi-grid">
      <KpiCard
        label="Total sign-ups"
        value={fmtN(total)}
        sub={workspaceHint}
        info="Distinct users in cohort. The user_roles spine returns one row per (user, workspace); a user active in multiple workspaces still counts once here. Activity is attributed to the workspace where it happened."
      />
      <KpiCard label="Active users" value={fmtN(active)} sub={fmtPct(active, total)} tone="success" info="Has at least one transcript or summary in any workspace." />
      <KpiCard label="Inactive users" value={fmtN(inactive)} sub={fmtPct(inactive, total)} tone="muted" info="No transcripts and no summaries recorded across any workspace." />
      <KpiCard
        label="Sign-up to Paid (Demo)"
        value={fmtPct(demoConverted, demoUsers.length)}
        sub={`${fmtN(demoConverted)} / ${fmtN(demoUsers.length)}`}
        tone="primary"
        info="Demo path = user had a demo at any time (lifetime had_demo). Paid = plus or pro plan in any workspace."
      />
      <KpiCard
        label="Sign-up to Paid (Self-serve)"
        value={fmtPct(selfConverted, selfUsers.length)}
        sub={`${fmtN(selfConverted)} / ${fmtN(selfUsers.length)}`}
        tone="primary"
        info="Self-serve = user never had a demo. Paid = plus or pro plan in any workspace."
      />
    </div>
  );
}
